import React, { useEffect, useState } from 'react';
import "./RatingsOfMySongBody.css";
import { useStateValue } from "../StateProvider";
import RatingBox from './RatingBox.js';
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore';
import { firestoreV9, firestore } from '../firebase';
import { collection, query, where } from 'firebase/firestore';

function RatingsOfMySongBody({spotify}) {
  const [{ userstartseite, userEmail, firebaseUid, firebaseUser, token}, dispatch] = useStateValue();
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [savingPreference, setSavingPreference] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(0); // First accordion (index 0) is expanded by default

  // Fetch user ID from Spotify if not already set
  useEffect(() => {
    if ((!userstartseite.id || userstartseite.id === "nochnicht") && spotify) {
      spotify.getMe().then((response) => {
        dispatch({
          type: "SET_USERSTARTSEITE",
          userstartseite: response,
        });
      }).catch((error) => {
        console.error("Error fetching Spotify user:", error);
      });
    }
  }, [userstartseite.id, spotify, dispatch]);

  // Check if user IDs are valid before querying
  const hasValidFirebaseUid = firebaseUid && firebaseUid !== "nochnicht";
  const hasValidSpotifyId = userstartseite.id && userstartseite.id !== "nochnicht";

  // Query for ratings with Firebase UID (new system)
  const entitledRatingsFirebaseRef = hasValidFirebaseUid
    ? query(collection(firestoreV9, 'entitledRatingsPerSongPerUser'), where('fromspotifyuser', '==', firebaseUid))
    : null;

  // Query for ratings with Spotify User ID (old system)
  const entitledRatingsSpotifyRef = hasValidSpotifyId
    ? query(collection(firestoreV9, 'entitledRatingsPerSongPerUser'), where('fromspotifyuser', '==', userstartseite.id))
    : null;

  const [entitledRatingsFirebase, loadingFirebase] = useCollectionDataOnce(entitledRatingsFirebaseRef);
  const [entitledRatingsSpotify, loadingSpotify] = useCollectionDataOnce(entitledRatingsSpotifyRef);

  // Merge results from both queries and remove duplicates
  const entitledRatings = React.useMemo(() => {
    if (!entitledRatingsFirebase && !entitledRatingsSpotify) return null;

    const allRatings = [
      ...(entitledRatingsFirebase || []),
      ...(entitledRatingsSpotify || [])
    ];

    // Remove duplicates based on track ID and user ID combination
    // Keep the entry with the highest entitled ratings (most recent)
    const uniqueRatingsMap = new Map();

    allRatings.forEach(rating => {
      const key = `${rating.trackIdOfSong}-${rating.fromspotifyuser}`;
      const existing = uniqueRatingsMap.get(key);

      if (!existing || (rating.entitledRatings > existing.entitledRatings)) {
        uniqueRatingsMap.set(key, rating);
      }
    });

    return Array.from(uniqueRatingsMap.values());
  }, [entitledRatingsFirebase, entitledRatingsSpotify]);

  const loading = loadingFirebase || loadingSpotify;

  // Fetch user's email notification preference
  useEffect(() => {
    const fetchEmailPreference = async () => {
      if (userEmail) {
        try {
          const prefDoc = await firestore.collection('userEmailPreferences').doc(userEmail).get();
          if (prefDoc.exists) {
            const data = prefDoc.data();
            setEmailNotificationsEnabled(data.receiveRatingEmails !== false); // Default to true if not set
          }
        } catch (error) {
          console.error('Error fetching email preference:', error);
        }
      }
    };

    fetchEmailPreference();
  }, [userEmail]);

  // Handle checkbox change
  const handleEmailPreferenceChange = async (event) => {
    const isEnabled = event.target.checked;
    setEmailNotificationsEnabled(isEnabled);
    setSavingPreference(true);

    if (userEmail) {
      try {
        await firestore.collection('userEmailPreferences').doc(userEmail).set({
          receiveRatingEmails: isEnabled,
          updatedAt: new Date(),
          userFirebaseId: firebaseUid
        }, { merge: true });
      } catch (error) {
        console.error('Error saving email preference:', error);
        // Revert on error
        setEmailNotificationsEnabled(!isEnabled);
      } finally {
        setSavingPreference(false);
      }
    } else {
      console.error('No userEmail available to save preference');
      setSavingPreference(false);
    }
  };

  // Show connect message if not fully authenticated
  if (!firebaseUser || !token) {
    return (
      <div className="feedbackBody">
        <div>
          <h1>
            <p><span role="img" aria-label="star">⭐</span> My Ratings</p>
          </h1>
          <div className="smallFont">
            <p>Here only you will see the ratings your songs received after you added them. Please login to get started.<br/><br/></p>
          </div>

          <div style={{ textAlign: 'center', margin: '30px 0' }}>
            <a href="/" className="landing-login-btn" style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              backgroundColor: '#90ee90',
              color: '#000000',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '700',
              textDecoration: 'none',
              borderRadius: '0',
              boxShadow: 'none',
              cursor: 'pointer',
              border: 'none',
              transition: 'all 0.3s ease'
            }}>
              START HERE
            </a>
            <p style={{ fontSize: '13px', color: '#888', marginTop: '16px', lineHeight: '1.5' }}>
              Get started by logging in to Song Carousel
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedbackBody">
      <div>
        <h1>
          <p><span role="img" aria-label="star">⭐</span> My Ratings</p></h1>
         <div className="smallFont"> <p>Only you will see the ratings you receive. Do not take negative ratings personal. New Rating "🔥Added to my playlist!" exists since Oct 10, 2025. <br/><br/></p></div>

        {/* Email Notification Preference */}
        <div style={{
          backgroundColor: '#000000',
          padding: '15px',
          borderRadius: '8px',
          margin: '15px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <input
            type="checkbox"
            id="emailNotifications"
            checked={emailNotificationsEnabled}
            onChange={handleEmailPreferenceChange}
            disabled={savingPreference || !userEmail}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer'
            }}
          />
          <label
            htmlFor="emailNotifications"
            style={{
              cursor: 'pointer',
              fontSize: '14px',
              flex: 1
            }}
          >
            Receive an Email when I get a new Rating {userEmail && <span style={{color: '#666'}}>({userEmail})</span>}
          </label>
          {savingPreference && <span style={{fontSize: '12px', color: '#999'}}>Saving...</span>}
        </div>

        <h4>Every added song will receive 5 ratings.<br /><br />You can add several songs and/or the same song several times.<br/></h4>
        <div style={{ marginBottom: '30px' }}></div>
      </div>
      <div className="feedbackContainer">
        {(!hasValidFirebaseUid && !hasValidSpotifyId) ? (
          <div><h4>Loading user information...</h4></div>
        ) : loading ? (
          <div><h4>Loading ratings...</h4></div>
        ) : !entitledRatings || entitledRatings.length === 0 ? (
          <div><h4>No ratings yet. Add a song to get started!</h4></div>
        ) : (
          entitledRatings.map((rating, index) => (
            <RatingBox
              key={rating.id}
              userid={firebaseUid || userstartseite.id}
              trackForBox={rating}
              isExpanded={expandedAccordion === index}
              onToggle={() => setExpandedAccordion(expandedAccordion === index ? -1 : index)}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default RatingsOfMySongBody;
